import {
    Paper,
    IconButton,
    Typography,
    Chip,
} from '@material-ui/core';
import { BidStatusChip, CalendarDateDisplay } from '@commonClient2/components';
import { useTranslation } from 'react-i18next';
import Banner from '../Banner';
import './DatedCard.scss';
import { UkgDateFormat } from '../../Enums';
import DateFormatService from '../../services/DateFormatService';
import ProtectedStateIcon from '../ProtectedStateIcon';

/**
 * @typedef {Object} SequentialBannerProps
 * @property {string} bannerColor
 * @property {string} fontColor
 * @property {string} bannerLabel
 * @property {boolean} hasTimer
 * @property {Date|null} sourceEndTime
 * @property {string} timerEndedText
 * @property {string} size
 *
 * @param {{
 * title: string,
 * subtitle: string,
 * subtitleLine2?: string,
 * subtitleColor: string,
 * note: string,
 * date: moment.Moment,
 * onClick: import('react').MouseEventHandler<HTMLButtonElement>
 * statusName: string
 * lastSavedOn: string
 * sequentialBannerProps?: SequentialBannerProps
 * employeeAddedAfterClose?: boolean
 * employeeSubmissionStartDate?: Date
 * chipLabel: string
 * isLocked: boolean
 * isShowProtectedIcon: boolean
 * }} props
 */
function DatedCard(props) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee.common.banner' });
    const {
        title,
        subtitle,
        subtitleLine2,
        subtitleColor,
        note,
        date,
        onClick,
        chipLabel = '',
        isLocked = false,
        isShowProtectedIcon = false,
        statusName,
        lastSavedOn,
        sequentialBannerProps,
        employeeAddedAfterClose,
        employeeSubmissionStartDate,
    } = props;
    const colorStyle = { color: `${subtitleColor}` };
    return (
        <IconButton
            className="dated-card-button-root"
            onClick={onClick}
        >
            <Paper className="dated-card-paper">
                <div className="dated-card-container">
                    <div className="dated-card-info-container">
                        <div>
                            {chipLabel && (
                                <Chip
                                    variant="outlined"
                                    size="small"
                                    label={chipLabel}
                                    className="dated-card-rebid-chip"
                                />
                            )}
                            {isShowProtectedIcon && (
                                <ProtectedStateIcon
                                    isProtected={isLocked}
                                    size="small"
                                />
                            )}
                        </div>
                        <Typography
                            variant="h6"
                            className="dated-card-header-title"
                        >
                            {title}
                        </Typography>
                        <Typography
                            className="dated-card-note"
                        >
                            {note}
                        </Typography>
                        <div>
                            <div className="dated-card-status-container">
                                <BidStatusChip statusName={statusName} />
                                {subtitleLine2 && (
                                    <Typography
                                        className="dated-card-header-subtitle2"
                                        gutterBottom
                                    >
                                        {subtitleLine2}
                                    </Typography>
                                )}
                            </div>
                            <Typography
                                style={colorStyle}
                                className="dated-card-header-subtitle"
                                gutterBottom
                            >
                                {subtitle}
                            </Typography>
                            {lastSavedOn && (
                                <Typography
                                    className="dated-card-last-saved"
                                    gutterBottom
                                >
                                    {lastSavedOn}
                                </Typography>
                            )}
                            {sequentialBannerProps && (
                                <Typography
                                    variant="body2"
                                    className="dated-card-note"
                                    gutterBottom
                                >
                                    {
                                        employeeAddedAfterClose
                                            ? t('appended')
                                            // eslint-disable-next-line max-len
                                            : t('priorityInTheFuture', { date: DateFormatService.formatDateOrEmpty(employeeSubmissionStartDate, UkgDateFormat.LongDateTime) })
                                    }
                                </Typography>
                            )}
                        </div>
                    </div>
                    <CalendarDateDisplay date={date.toDate()} />
                </div>
                {sequentialBannerProps && (
                    <Banner
                        bannerColor={sequentialBannerProps.bannerColor}
                        fontColor={sequentialBannerProps.fontColor}
                        bannerLabel={sequentialBannerProps.bannerLabel}
                        hasTimer={sequentialBannerProps.hasTimer}
                        sourceEndTime={sequentialBannerProps.sourceEndTime}
                        timerEndedText={sequentialBannerProps.timerEndedText}
                        size="sm"
                    />
                )}
            </Paper>
        </IconButton>

    );
}

export default DatedCard;
